<template>
  <section class="mc-rec-panel mc-rec-panel--chart h-100 d-flex flex-column">
    <div class="mc-rec-panel__head">
      <div class="mc-rec-panel__head-text">
        <h2 class="mc-rec-panel__title">
          Evolução da performance
        </h2>
        <p class="mc-rec-panel__subtitle">
          Tendências no período filtrado.
        </p>
      </div>
      <div
        v-if="aba !== 'custo' && trendSemanaPct != null"
        class="mc-rec-trend-pill"
        :class="trendSemanaPct >= 0 ? 'mc-rec-trend-pill--up' : 'mc-rec-trend-pill--down'"
      >
        <span class="mc-rec-trend-pill__value">{{ trendSemanaPct >= 0 ? '↑' : '↓' }} {{ Math.abs(trendSemanaPct) }}%</span>
        <span class="mc-rec-trend-pill__hint">vs sem. ant.</span>
      </div>
    </div>

    <div class="mc-rec-surface mc-rec-surface--chart flex-grow-1 d-flex flex-column p-3">
      <b-nav pills class="mc-rec-tabs mc-rec-tabs--chart mc-rec-tabs--chart-padded flex-wrap mb-3">
        <b-nav-item
          v-for="t in tabs"
          :key="t.id"
          :active="aba === t.id"
          @click="aba = t.id"
        >
          {{ t.label }}
        </b-nav-item>
      </b-nav>
      <div
        class="mc-rec-chart-wrap mc-rec-chart-wrap--tall flex-grow-1"
        :class="{ 'mc-rec-chart-wrap--with-legend': aba === 'custo' }"
      >
        <McRecommendationMixedChart
          v-if="aba !== 'custo' && mixedChartData && mixedChartData.labels && mixedChartData.labels.length"
          :key="'mix-' + aba"
          :mixed-data="mixedChartData"
          :mixed-options="mixedChartOptions"
        />
        <ChartjsComponentBarChart
          v-else-if="aba === 'custo' && chartDataCusto.labels && chartDataCusto.labels.length"
          :key="'custo-' + aba"
          :data="chartDataCusto"
          :options="chartOptionsCusto"
        />
        <p
          v-else
          class="text-muted text-center py-5 mb-0 d-flex align-items-center justify-content-center h-100"
        >
          Sem dados para este gráfico no período.
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import { BNav, BNavItem } from 'bootstrap-vue'
import ChartjsComponentBarChart from '@/views/dashboard/notification/charts-components/ChartjsComponentBarChart.vue'
import McRecommendationMixedChart from './McRecommendationMixedChart.vue'

function interpolateBarColor(index, total) {
  const t = total <= 1 ? 0 : index / (total - 1)
  const r = Math.round(91 + (242 - 91) * t)
  const g = Math.round(2 + (95 - 2) * t)
  const b = Math.round(140 + (92 - 140) * t)
  return `rgba(${r},${g},${b},0.52)`
}

/** Cor única das barras em "Receita por semana" (alinhada à identidade roxa do painel). */
const BAR_RECEITA_SOLID = 'rgba(91, 2, 140, 0.52)'

function roundMoney2(n) {
  const x = Number(n)
  if (Number.isNaN(x)) return 0
  return Math.round(x * 100) / 100
}

export default {
  name: 'RecommendationPerformanceCharts',
  components: {
    BNav,
    BNavItem,
    ChartjsComponentBarChart,
    McRecommendationMixedChart,
  },
  props: {
    chartReceita: {
      type: Object,
      default: () => ({ labels: [], data: [] }),
    },
    chartConversao: {
      type: Object,
      default: () => ({ labels: [], data: [] }),
    },
    chartCusto: {
      type: Object,
      default: () => ({ labels: [], data: [] }),
    },
  },
  data() {
    return {
      aba: 'receita',
      tabs: [
        { id: 'receita', label: 'Receita por semana' },
        { id: 'conversao', label: 'Conversão por semana' },
        { id: 'custo', label: 'Custo x receita' },
      ],
    }
  },
  computed: {
    currentPayloadMeta() {
      const map = {
        receita: { label: 'Receita (R$)', mode: 'currency', payload: this.chartReceita },
        conversao: { label: 'Conversões', mode: 'number', payload: this.chartConversao },
      }
      return map[this.aba] || map.receita
    },
    trendSemanaPct() {
      if (this.aba === 'custo') return null
      const p = this.currentPayloadMeta.payload || { data: [] }
      const d = (p.data || []).map(v => Number(v) || 0)
      if (d.length < 2) return null
      const prev = d[d.length - 2]
      const last = d[d.length - 1]
      if (prev === 0 && last === 0) return null
      if (prev === 0) return last > 0 ? 100 : null
      return Math.round(((last - prev) / Math.abs(prev)) * 100)
    },
    mixedChartData() {
      if (this.aba === 'custo') return null
      const cfg = this.currentPayloadMeta
      const p = cfg.payload || { labels: [], data: [] }
      const rawLabels = p.labels || []
      const rawVals = (p.data || []).map(v => Number(v) || 0)
      const values = cfg.mode === 'currency' ? rawVals.map(roundMoney2) : rawVals
      const n = Math.max(values.length, 1)
      const labels = values.map((v, i) => {
        const primary = this.formatPrimary(cfg.mode, v)
        const sub = rawLabels[i] != null ? String(rawLabels[i]) : `Sem ${i + 1}`
        return `${primary}\n${sub}`
      })
      const barBg = this.aba === 'receita'
        ? values.map(() => BAR_RECEITA_SOLID)
        : values.map((_, i) => interpolateBarColor(i, n))
      return {
        labels,
        datasets: [
          {
            type: 'line',
            label: 'Tendência',
            data: values,
            fill: true,
            backgroundColor: 'rgba(91, 2, 140, 0.1)',
            borderColor: 'rgba(242, 95, 92, 0.9)',
            borderWidth: 2.5,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: '#f25f5c',
            pointHoverBorderColor: '#fff',
            pointHoverBorderWidth: 2,
            lineTension: 0.35,
          },
          {
            type: 'bar',
            label: cfg.label,
            data: values,
            backgroundColor: barBg,
            borderWidth: 0,
          },
        ],
      }
    },
    mixedChartOptions() {
      const { mode } = this.currentPayloadMeta
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        layout: {
          padding: {
            top: 20,
            right: 10,
            left: 4,
            bottom: 4,
          },
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          filter(tooltipItem) {
            return tooltipItem.datasetIndex === 1
          },
          backgroundColor: 'rgba(34, 28, 53, 0.94)',
          titleFontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
          bodyFontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
          titleFontSize: 12,
          bodyFontSize: 13,
          xPadding: 14,
          yPadding: 10,
          cornerRadius: 12,
          callbacks: {
            title: items => {
              if (!items || !items.length) return ''
              const raw = items[0].xLabel
              return String(raw).replace(/\n/g, ' · ')
            },
            label: (tooltipItem, data) => {
              const ds = data.datasets[tooltipItem.datasetIndex]
              const val = ds.data[tooltipItem.index]
              return ` ${ds.label}: ${this.formatTooltip(mode, val)}`
            },
          },
        },
        hover: { mode: 'index', intersect: false },
        scales: {
          xAxes: [{
            offset: true,
            barPercentage: 0.58,
            categoryPercentage: 0.72,
            gridLines: { display: false, drawBorder: false, tickMarkLength: 0 },
            ticks: {
              fontColor: '#6e6781',
              fontSize: 10,
              maxRotation: 0,
              autoSkip: false,
              padding: 6,
            },
          }],
          yAxes: [{
            gridLines: {
              color: 'rgba(91, 2, 140, 0.07)',
              drawBorder: false,
              zeroLineColor: 'rgba(91, 2, 140, 0.12)',
            },
            ticks: {
              beginAtZero: true,
              fontColor: '#9b93af',
              fontSize: 11,
              padding: 10,
              maxTicksLimit: 6,
              callback: value => this.formatYAxis(mode, value),
            },
          }],
        },
      }
    },
    chartDataCusto() {
      const labels = this.chartReceita.labels || this.chartCusto.labels || []
      const custo = this.chartCusto.data || []
      const receita = this.chartReceita.data || []
      return {
        labels,
        datasets: [
          {
            label: 'Custo (R$)',
            data: labels.map((_, i) => roundMoney2(custo[i] != null ? custo[i] : 0)),
            backgroundColor: 'rgba(242, 95, 92, 0.38)',
            borderColor: 'rgba(242, 95, 92, 0.85)',
            borderWidth: 0,
          },
          {
            label: 'Receita (R$)',
            data: labels.map((_, i) => roundMoney2(receita[i] != null ? receita[i] : 0)),
            backgroundColor: 'rgba(91, 2, 140, 0.35)',
            borderColor: 'rgba(91, 2, 140, 0.85)',
            borderWidth: 0,
          },
        ],
      }
    },
    chartOptionsCusto() {
      const fmtBrl2 = v => {
        const n = roundMoney2(v)
        return n.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      }
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'bottom',
          fullWidth: true,
          labels: {
            fontColor: '#6e6781',
            fontSize: 11,
            boxWidth: 10,
            padding: 12,
            usePointStyle: false,
          },
        },
        layout: {
          padding: {
            top: 12,
            right: 16,
            left: 12,
            bottom: 4,
          },
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(34, 28, 53, 0.94)',
          titleFontSize: 12,
          bodyFontSize: 13,
          xPadding: 14,
          yPadding: 10,
          cornerRadius: 12,
          callbacks: {
            label: (tooltipItem, data) => {
              const ds = data.datasets[tooltipItem.datasetIndex]
              const val = roundMoney2(ds.data[tooltipItem.index])
              return ` ${ds.label}: ${fmtBrl2(val)}`
            },
          },
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              fontColor: '#6e6781',
              callback: v => fmtBrl2(v),
            },
            gridLines: { color: 'rgba(91,2,140,0.06)' },
          }],
          xAxes: [{
            display: true,
            ticks: { fontColor: '#5e5873', maxRotation: 45, padding: 6 },
            gridLines: { display: false, drawBorder: true },
          }],
        },
      }
    },
  },
  methods: {
    formatPrimary(mode, v) {
      const n = Number(v) || 0
      if (mode === 'currency') {
        return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
      }
      if (mode === 'integer') return String(Math.round(n))
      return n % 1 === 0 ? String(Math.round(n)) : n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
    },
    formatYAxis(mode, value) {
      const n = Number(value) || 0
      if (mode === 'currency') {
        if (Math.abs(n) >= 1000) {
          return `R$ ${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`
        }
        return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
      }
      if (mode === 'integer') return Math.round(n)
      return n % 1 === 0 ? Math.round(n) : n.toFixed(1)
    },
    formatTooltip(mode, v) {
      const n = Number(v) || 0
      if (mode === 'currency') {
        return n.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      }
      if (mode === 'integer') return String(Math.round(n))
      return n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
    },
  },
}
</script>
